// @ts-nocheck
/**
 * MaskedCanvasOverlay - Canvas-based masked image overlay for polygon/module clipping
 *
 * STATUS: DORMANT - Currently not in active use (all callers pass maskPolygons={null})
 * Preserved for potential future use in flux visualization with module masking.
 *
 * @see GeoTiffImageOverlay - Uses this component when maskPolygons is provided
 */

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

export interface MaskedCanvasOverlayProps {
  url: string;
  bounds: L.LatLngBoundsExpression;
  opacity: number;
  layer: string;
  maskPolygons: [number, number][][]; // Array of polygons (modules or single roof polygon)
  onLoadingChange?: (status: 'loading' | 'success' | 'error') => void;
}

export function MaskedCanvasOverlay({
  url,
  bounds: imageBounds,
  opacity,
  layer,
  maskPolygons,
  onLoadingChange
}: MaskedCanvasOverlayProps) {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  // Load image
  useEffect(() => {
    if (onLoadingChange) onLoadingChange('loading');

    const img = new Image();
    img.crossOrigin = 'anonymous'; // Enable CORS
    img.onload = () => {
      setImage(img);
      if (onLoadingChange) onLoadingChange('success');
    };
    img.onerror = (e) => {
      console.error(`❌ [MASKED OVERLAY] Failed to load ${layer}:`, e);
      if (onLoadingChange) onLoadingChange('error');
    };
    img.src = url;
  }, [url, layer]); // Removed onLoadingChange to prevent infinite loops

  // Redraw canvas when image loads or map moves/zooms
  useEffect(() => {
    if (!image || !canvasRef.current || !maskPolygons || maskPolygons.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const redraw = () => {
      // Get map bounds in pixels
      const mapBounds = map.getBounds();
      const mapSize = map.getSize();

      // Set canvas size to match map
      canvas.width = mapSize.x;
      canvas.height = mapSize.y;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create composite clipping path from all polygons (modules or roof polygon)
      ctx.save();
      ctx.beginPath();

      maskPolygons.forEach(polygon => {
        polygon.forEach(([lat, lng], index) => {
          const point = map.latLngToContainerPoint([lat, lng]);
          if (index === 0) {
            ctx.moveTo(point.x, point.y);
          } else {
            ctx.lineTo(point.x, point.y);
          }
        });
        ctx.closePath();
      });

      ctx.clip(); // Single clip operation for all polygons

      // Get image bounds
      const bounds = L.latLngBounds(imageBounds as any);
      const topLeft = map.latLngToContainerPoint(bounds.getNorthWest());
      const bottomRight = map.latLngToContainerPoint(bounds.getSouthEast());

      const width = bottomRight.x - topLeft.x;
      const height = bottomRight.y - topLeft.y;

      // Draw image with opacity
      ctx.globalAlpha = opacity;
      ctx.drawImage(image, topLeft.x, topLeft.y, width, height);

      ctx.restore();
    };

    redraw();

    // Redraw on map events
    map.on('move zoom viewreset', redraw);

    return () => {
      map.off('move zoom viewreset', redraw);
    };
  }, [image, map, maskPolygons, imageBounds, opacity]);

  // Get overlay pane element for portal
  const overlayPane = map.getPane('overlayPane');

  // Use portal to render canvas in overlayPane (managed by React, not manual DOM)
  return overlayPane ? createPortal(
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        // No z-index needed - inherits from overlayPane (400), modules pane (500) will render above
      }}
    />,
    overlayPane
  ) : null;
}

export default MaskedCanvasOverlay;
